import {
  // General Schemas
  // Login endpoint
  loginRequestSchema,
  loginSuccessResponseSchema,
  // Me endpoint
  userSuccessResponseSchema,
  // Register endpoint
  createUserRequestSchema,
  userCreateResponseSchema,
  // Confirm Email endpoint
  confirmEmailRequestSchema,
  confirmEmailResponseSchema,
  // Set Phone number endpoint
  generatePhoneVerificationRequestSchema,
  generatePhoneVerificationResponse,
  // Confirm Phone Number endpoint
  confirmPhoneRequestSchema,
  confirmPhoneResponseSchema,
  forgotPasswordResponseSchema,
  forgotPasswordRequestSchema,
  passwordResetResponseSchema,
  passwordResetRequestSchema,
  updatePasswordRequestSchema,
  updatePasswordResponseSchema,
  loginErrorResponseSchema,
  userEmailExistsResponseSchema,
  successResponseSchema,
  idAustriaLoginRequest,
  idAustriaLoginSuccess,
  idAustriaLoginToRegisterResponse,
  idAustriaFinishLoginRequest,
  paymentMethodsResponseSchema,
  commonErrorCodeResponseSchema,
  notFoundResponseSchema,
  paymentSetupIntentResponseSchema,
  setDefaultPaymentMethodRequestSchema,
  setDefaultPaymentMethodResponseSchema,
  getEmailNotificationResponseSchema,
  permissionDeniedResponseSchema,
  createEmailNotificationSchema,
  updateUserDetailsRequestSchema,
  updateUserDetailsResponseSchema,
  unprocessableEntityResponseSchema,
  userDataUpdateVerificationRequestSchema,
  userActivityResponseSchema,
  badRequestResponseSchema,
  unauthorizedResponseSchema,
  setAccountTypeRequestSchema,
  setAccountTypeResponseSchema,
} from 'xosign-types';

import { z } from 'zod';
import { API_URL_BASE } from '@/config';
import { type EndpointDefinition } from '@/types/endpointTypes';

// Extend request objects only for the frontend with necessary path params
const CRUD = {
  r: z.object({ memberId: z.string() }),
  n: z.object({ id: z.string() }),
};

const notificationSaveSchema = CRUD.n.merge(createEmailNotificationSchema);
const updateUserPhoneNumber = CRUD.n.merge(
  userDataUpdateVerificationRequestSchema
);
const userUpdateRequestSchema = CRUD.n.merge(updateUserDetailsRequestSchema);

const endpoints = {
  'users.login': {
    method: 'POST',
    url: `${API_URL_BASE}auth/login`,
    requestSchema: loginRequestSchema,
    responseSuccessSchema: loginSuccessResponseSchema,
    responseErrorSchemas: {
      401: loginErrorResponseSchema,
    },
    requiresToken: 'none',
  } satisfies EndpointDefinition<
    typeof loginRequestSchema,
    typeof loginSuccessResponseSchema,
    {
      401: typeof loginErrorResponseSchema;
    }
  >,
  'users.me': {
    method: 'GET',
    url: `${API_URL_BASE}users/me`,
    requestSchema: z.undefined(),
    responseSuccessSchema: userSuccessResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof userSuccessResponseSchema,
    z.ZodUndefined
  >,
  'users.register': {
    method: 'POST',
    url: `${API_URL_BASE}users`, // /routes/users/create-user.ts
    requestSchema: createUserRequestSchema,
    responseSuccessSchema: userCreateResponseSchema,
    requiresToken: 'none',
    responseErrorSchemas: {
      409: userEmailExistsResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof createUserRequestSchema,
    typeof userCreateResponseSchema,
    {
      409: typeof userEmailExistsResponseSchema;
    }
  >,
  'users.register.confirmEmail': {
    method: 'POST',
    url: `${API_URL_BASE}auth/confirm-email`, // /routes/auth/confirm-email.ts
    requestSchema: confirmEmailRequestSchema,
    responseSuccessSchema: confirmEmailResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: permissionDeniedResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof confirmEmailRequestSchema,
    typeof confirmEmailResponseSchema,
    {
      403: typeof permissionDeniedResponseSchema;
    }
  >,
  'users.register.accountType': {
    method: 'POST',
    url: `${API_URL_BASE}users/me/account-type`, // /routes/users/set-account-type.ts
    requestSchema: setAccountTypeRequestSchema,
    responseSuccessSchema: setAccountTypeResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
      403: permissionDeniedResponseSchema,
      422: setAccountTypeRequestSchema,
    },
  } satisfies EndpointDefinition<
    typeof setAccountTypeRequestSchema,
    typeof setAccountTypeResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      422: typeof setAccountTypeRequestSchema;
    }
  >,
  'users.register.confirmPhone': {
    method: 'POST',
    url: `${API_URL_BASE}auth/confirm-phone`,
    requestSchema: confirmPhoneRequestSchema,
    responseSuccessSchema: confirmPhoneResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: permissionDeniedResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof confirmPhoneRequestSchema,
    typeof confirmPhoneResponseSchema,
    {
      403: typeof permissionDeniedResponseSchema;
    }
  >,
  'users.verify.phone': {
    method: 'POST',
    url: `${API_URL_BASE}users/:id/verify-phone-change`,
    requestSchema: updateUserPhoneNumber,
    responseSuccessSchema: successResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof updateUserPhoneNumber,
    typeof successResponseSchema,
    z.ZodUndefined
  >,
  'users.password.forgot': {
    method: 'POST',
    url: `${API_URL_BASE}auth/forgot-password`,
    requestSchema: forgotPasswordRequestSchema,
    responseSuccessSchema: forgotPasswordResponseSchema,
    requiresToken: 'none',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof forgotPasswordRequestSchema,
    typeof forgotPasswordResponseSchema,
    z.ZodUndefined
  >,
  'users.password.reset': {
    method: 'POST',
    url: `${API_URL_BASE}auth/reset-password`,
    requestSchema: passwordResetRequestSchema,
    responseSuccessSchema: passwordResetResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof passwordResetRequestSchema,
    typeof passwordResetResponseSchema,
    z.ZodUndefined
  >,
  'users.register.setPhone': {
    method: 'POST',
    url: `${API_URL_BASE}auth/generate-phone-verification`,
    requestSchema: generatePhoneVerificationRequestSchema,
    responseSuccessSchema: generatePhoneVerificationResponse,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      422: unprocessableEntityResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof generatePhoneVerificationRequestSchema,
    typeof generatePhoneVerificationResponse,
    {
      400: typeof badRequestResponseSchema;
      422: typeof unprocessableEntityResponseSchema;
    }
  >,
  'users.password.change': {
    method: 'POST',
    url: `${API_URL_BASE}users/update-password`,
    requestSchema: updatePasswordRequestSchema,
    responseSuccessSchema: updatePasswordResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof updatePasswordRequestSchema,
    typeof updatePasswordResponseSchema,
    z.ZodUndefined
  >,
  'users.suspend': {
    method: 'POST',
    url: `${API_URL_BASE}users/:memberId/suspend`,
    requestSchema: CRUD.r,
    responseSuccessSchema: successResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: badRequestResponseSchema,
      401: unauthorizedResponseSchema,
      403: permissionDeniedResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof successResponseSchema,
    {
      400: typeof badRequestResponseSchema;
      401: typeof unauthorizedResponseSchema;
      403: typeof permissionDeniedResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'users.login.idAustria': {
    method: 'POST',
    url: `${API_URL_BASE}auth/external/idaustria`,
    requestSchema: idAustriaLoginRequest,
    responseSuccessSchema: idAustriaLoginSuccess,
    requiresToken: 'none',
    responseErrorSchemas: {
      401: idAustriaLoginToRegisterResponse,
    },
  } satisfies EndpointDefinition<
    typeof idAustriaLoginRequest,
    typeof idAustriaLoginSuccess,
    {
      401: typeof idAustriaLoginToRegisterResponse;
    }
  >,
  'users.login.idAustria.finish': {
    method: 'POST',
    url: `${API_URL_BASE}auth/external/idaustria/finish`,
    requestSchema: idAustriaFinishLoginRequest,
    responseSuccessSchema: userCreateResponseSchema,
    requiresToken: 'none',
    responseErrorSchemas: {
      409: userEmailExistsResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof idAustriaFinishLoginRequest,
    typeof userCreateResponseSchema,
    {
      409: typeof userEmailExistsResponseSchema;
    }
  >,
  'users.me.cards': {
    method: 'GET',
    url: `${API_URL_BASE}payment/methods`,
    requestSchema: z.undefined(),
    responseSuccessSchema: paymentMethodsResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof paymentMethodsResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
    }
  >,
  'users.cards.add': {
    method: 'POST',
    url: `${API_URL_BASE}payment/setup-intent`,
    requestSchema: z.undefined(),
    responseSuccessSchema: paymentSetupIntentResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof paymentSetupIntentResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'users.cards.default': {
    method: 'POST',
    url: `${API_URL_BASE}payment/default-method`,
    requestSchema: setDefaultPaymentMethodRequestSchema,
    responseSuccessSchema: setDefaultPaymentMethodResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof setDefaultPaymentMethodRequestSchema,
    typeof setDefaultPaymentMethodResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
    }
  >,
  'users.get.me.notifications': {
    method: 'GET',
    url: `${API_URL_BASE}users/:id/email-notifications`,
    requestSchema: CRUD.n,
    responseSuccessSchema: getEmailNotificationResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      404: notFoundResponseSchema,
      403: permissionDeniedResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.n,
    typeof getEmailNotificationResponseSchema,
    {
      404: typeof notFoundResponseSchema;
      403: typeof permissionDeniedResponseSchema;
    }
  >,
  'users.set.me.notifications': {
    method: 'POST',
    url: `${API_URL_BASE}users/:id/email-notifications`,
    requestSchema: notificationSaveSchema,
    responseSuccessSchema: getEmailNotificationResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      404: notFoundResponseSchema,
      403: permissionDeniedResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof notificationSaveSchema,
    typeof getEmailNotificationResponseSchema,
    {
      404: typeof notFoundResponseSchema;
      403: typeof permissionDeniedResponseSchema;
    }
  >,
  'user.me.change.details': {
    method: 'PATCH',
    url: `${API_URL_BASE}users/:id/update-details`,
    requestSchema: userUpdateRequestSchema,
    responseSuccessSchema: updateUserDetailsResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      422: unprocessableEntityResponseSchema,
      403: permissionDeniedResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof userUpdateRequestSchema,
    typeof updateUserDetailsResponseSchema,
    {
      422: typeof unprocessableEntityResponseSchema;
      403: typeof permissionDeniedResponseSchema;
    }
  >,
  'users.get.activity': {
    method: 'GET',
    url: `${API_URL_BASE}users/me/activity`,
    requestSchema: z.undefined(),
    responseSuccessSchema: userActivityResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: permissionDeniedResponseSchema,
    },
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof userActivityResponseSchema,
    {
      403: typeof permissionDeniedResponseSchema;
    }
  >,
};

export default endpoints;
